import React from 'react'
import PropTypes from 'prop-types'
import Typography from '../Basics/Typography'
import useBasePagination from './Pagination.hook'
import { RootPagination, PaginationItem, PaginationWrapper, Strong } from './Pagination.styles'

export const usePagination = useBasePagination

/**
 * The Pagination's component.
 */
const Pagination = props => {
  const {
    alignment,
    boundaryCount,
    count,
    defaultPage,
    disabled,
    disableFirstButton,
    disableLastButton,
    disableNextButton,
    disablePrevButton,
    disableResults,
    labels,
    page,
    records,
    siblingCount,
    size,
    total,
    variant,
    onChange,
    ...restProps
  } = props
  const renderItem = item => <PaginationItem shape="rounded" {...item} />

  return (
    <PaginationWrapper alignment={alignment}>
      {alignment !== 'left' && !disableResults && (
        <Typography color="black2" margin={alignment === 'center' && '0 0 8px'} variant="body2">
          {`${labels.showing} `}
          <Strong>{records}</Strong>
          {` ${labels.of} `}
          <Strong>{total}</Strong>
          {total === 1 && ` ${labels.results}`.slice(0, -1)}
          {total !== 1 && ` ${labels.results}`}
        </Typography>
      )}

      <RootPagination
        {...restProps}
        alignment={alignment}
        boundaryCount={boundaryCount}
        color="primary"
        count={count}
        disabled={disabled}
        hideNextButton={disableNextButton}
        hidePrevButton={disablePrevButton}
        showFirstButton={!disableFirstButton}
        showLastButton={!disableLastButton}
        siblingCount={siblingCount}
        page={page ?? defaultPage}
        shape="rounded"
        size={size}
        variant={variant}
        onChange={onChange ?? onChange}
        renderItem={renderItem}
      />

      {alignment === 'left' && !disableResults && (
        <Typography color="black2" variant="body2">
          {`${labels.showing} `}
          <Strong>{records}</Strong>
          {` ${labels.of} `}
          <Strong>{total}</Strong>
          {total === 1 && ` ${labels.results}`.slice(0, -1)}
          {total !== 1 && ` ${labels.results}`}
        </Typography>
      )}
    </PaginationWrapper>
  )
}

Pagination.defaultProps = {
  alignment: 'right',
  boundaryCount: 1,
  count: 1,
  defaultPage: 1,
  disabled: false,
  disableFirstButton: true,
  disableLastButton: true,
  disableNextButton: false,
  disablePrevButton: false,
  disableResults: true,
  labels: {
    of: 'de',
    results: 'resultados',
    showing: 'Mostrando'
  },
  siblingCount: 1,
  size: 'medium',
  variant: 'text',
  onChange: () => undefined
}

Pagination.propTypes = {
  /**
   * Alignment.
   */
  alignment: PropTypes.oneOf(['center', 'left', 'right']),
  /**
   * Boundary count.
   */
  boundaryCount: PropTypes.number,
  /**
   * Count.
   */
  count: PropTypes.number,
  /**
   * Default page.
   */
  defaultPage: PropTypes.number,
  /**
   * Disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Disable first button.
   */
  disableFirstButton: PropTypes.bool,
  /**
   * Disable last button.
   */
  disableLastButton: PropTypes.bool,
  /**
   * Disable next button.
   */
  disableNextButton: PropTypes.bool,
  /**
   * Disable prev button.
   */
  disablePrevButton: PropTypes.bool,
  /**
   * Disable results.
   */
  disableResults: PropTypes.bool,
  /**
   * Labels.
   */
  labels: PropTypes.shape({
    of: PropTypes.string,
    results: PropTypes.string,
    showing: PropTypes.string
  }),
  /**
   * Page.
   */
  page: PropTypes.number,
  /**
   * Records.
   */
  records: PropTypes.number,
  /**
   * Sibling count.
   */
  siblingCount: PropTypes.number,
  /**
   * Size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   * Total.
   */
  total: PropTypes.number,
  /**
   * Variant.
   */
  variant: PropTypes.oneOf(['outlined', 'text']),
  /**
   * On change.
   */
  onChange: PropTypes.func
}

export default Pagination
